/**
 * Google Meet Tutorial Page
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const translations: Record<Locale, TutorialData> = {
  en: {
    pageTitle: 'Using Sokuji with Google Meet',
    backLink: 'Back to Supported Sites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Overview',
      content:
        'This guide will walk you through setting up and using Sokuji with Google Meet for real-time language translation during your meetings.',
    },
    steps: [
      {
        title: 'Visit Google Meet',
        content:
          'Open your browser and navigate to <code>meet.google.com</code>. Join or start a meeting as you normally would.',
        screenshot: '1.png',
        tip: 'Make sure you have the Sokuji extension installed and enabled in Chrome or Edge.',
      },
      {
        title: 'Grant Microphone Permission',
        content:
          'If Sokuji requests microphone permission, click <strong>"Allow while visiting the site"</strong> to grant access.',
        screenshot: '2.png',
        tip: 'This permission is required for Sokuji to process your audio for translation.',
      },
      {
        title: 'Open Sokuji Extension',
        content:
          'Click the Sokuji extension icon in the top-right corner of your browser, then click <strong>"Open Sokuji"</strong> to launch the sidebar panel.',
        screenshot: '3.png',
        tip: 'The Sokuji sidebar will open on the right side of your browser window.',
      },
      {
        title: 'Configure Sokuji Settings',
        content:
          'In the Sokuji sidebar, configure your translation settings including source and target languages, AI provider, and other preferences.',
        screenshot: '4.png',
        tip: 'Test your microphone input to ensure Sokuji is receiving audio correctly.',
      },
      {
        title: 'Select Sokuji Virtual Microphone',
        content:
          "In Google Meet's audio settings, click the microphone icon and select <strong>Sokuji Virtual Microphone</strong> from the dropdown menu.",
        screenshot: '5.png',
        tip: "If you don't see the virtual microphone, refresh the page and ensure the extension is active.",
      },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Speak clearly and avoid background noise',
        'Pause briefly between sentences for better translation accuracy',
        'Test your audio before important meetings',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: "Why can't I see the Sokuji Virtual Microphone?",
          answer:
            "Make sure the Sokuji extension is installed and active. Try refreshing the Google Meet page. If the issue persists, check that you've granted microphone permissions to the extension.",
        },
        {
          question: 'Can other participants hear both languages?',
          answer:
            "By default, participants only hear the translated audio. If you turn on Passthrough in Sokuji's Speech block, participants hear your original voice under the translation at the same time. This is useful when you want listeners to hear both languages for context or verification.",
        },
        {
          question: 'Does Sokuji work with Google Meet recordings?',
          answer:
            "Yes, the translated audio will be included in the meeting recording if you're using Sokuji Virtual Microphone.",
        },
        {
          question: 'What is Passthrough?',
          answer:
            'Passthrough mixes your own voice, at a lower volume, under the translation into the virtual microphone, so listeners hear both languages at once. It is a sub-row of Translation the other side hears in the Speech block (Quick settings, or Settings → Advanced → Audio), with a volume slider: 20% by default, 60% at most. It is greyed out while Translation the other side hears is off. This is particularly useful for bilingual audiences or when you want to keep the emotional tone and nuance of your original speech.',
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content:
        'If you encounter any issues, please visit our <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub repository</a> or contact us at <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> for support.',
    },
  },
  zh: {
    pageTitle: '在 Google Meet 中使用 Sokuji',
    backLink: '返回支持的网站',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概述',
      content: '本指南将指导您如何在 Google Meet 中设置和使用 Sokuji，实现会议中的实时语言翻译。',
    },
    steps: [
      {
        title: '访问 Google Meet',
        content: '打开浏览器并访问 <code>meet.google.com</code>。像往常一样加入或开始会议。',
        screenshot: '1.png',
        tip: '确保您已在 Chrome 或 Edge 中安装并启用了 Sokuji 扩展。',
      },
      {
        title: '授予麦克风权限',
        content:
          '如果 Sokuji 请求麦克风权限，请点击<strong>"访问该网站时允许"</strong>以授予访问权限。',
        screenshot: '2.png',
        tip: '此权限是 Sokuji 处理您的音频以进行翻译所必需的。',
      },
      {
        title: '打开 Sokuji 扩展',
        content:
          '点击浏览器右上角的 Sokuji 扩展图标，然后点击<strong>"打开 Sokuji"</strong>以启动侧边栏面板。',
        screenshot: '3.png',
        tip: 'Sokuji 侧边栏将在浏览器窗口的右侧打开。',
      },
      {
        title: '配置 Sokuji 设置',
        content:
          '在 Sokuji 侧边栏中，配置您的翻译设置，包括源语言和目标语言、AI 提供商和其他偏好设置。',
        screenshot: '4.png',
        tip: '测试您的麦克风输入以确保 Sokuji 正确接收音频。',
      },
      {
        title: '选择 Sokuji 虚拟麦克风',
        content:
          '在 Google Meet 的音频设置中，点击麦克风图标并从下拉菜单中选择 <strong>Sokuji Virtual Microphone</strong>。',
        screenshot: '5.png',
        tip: '如果看不到虚拟麦克风，请刷新页面并确保扩展处于活动状态。',
      },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '清晰说话，避免背景噪音',
        '句子之间稍作停顿以提高翻译准确性',
        '重要会议前测试音频',
      ],
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: '为什么看不到 Sokuji 虚拟麦克风？',
          answer:
            '确保 Sokuji 扩展已安装并处于活动状态。尝试刷新 Google Meet 页面。如果问题仍然存在，请检查是否已授予扩展麦克风权限。',
        },
        {
          question: '其他参与者能听到两种语言吗？',
          answer:
            '默认情况下，参与者只能听到翻译后的音频。如果您在 Sokuji 的语音区块中打开“原声直通”，参与者会同时听到译音和垫在下面的您的原声。当您希望听众听到两种语言以便理解上下文或进行验证时，此功能非常有用。',
        },
        {
          question: 'Sokuji 支持 Google Meet 录制吗？',
          answer: '是的，如果您使用 Sokuji 虚拟麦克风，翻译后的音频将包含在会议录制中。',
        },
        {
          question: '什么是原声直通？',
          answer:
            '原声直通会把您的原声以较低音量混在译音下面，一起送进虚拟麦克风，让听众同时听到两种语言。它是语音区块（快捷设置，或设置 → 高级 → 音频）中“对方听到的翻译”下的子项，带一个音量滑块：默认 20%，最大 60%。“对方听到的翻译”关闭时，它会变灰。这对于双语听众或当您想要保持原始语音的情感语调和细微差别时特别有用。',
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content:
        '如果遇到任何问题，请访问我们的 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 仓库</a> 或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> 获取支持。',
    },
  },
  ja: {
    pageTitle: 'Google Meet で Sokuji を使用する',
    backLink: 'サポートされているサイトに戻る',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概要',
      content:
        'このガイドでは、Google Meet で Sokuji を設定して使用し、会議中にリアルタイム言語翻訳を行う方法を説明します。',
    },
    steps: [
      {
        title: 'Google Meet にアクセス',
        content:
          'ブラウザを開いて <code>meet.google.com</code> にアクセスします。通常どおり会議に参加または開始します。',
        screenshot: '1.png',
        tip: 'Chrome または Edge に Sokuji 拡張機能がインストールされ、有効になっていることを確認してください。',
      },
      {
        title: 'マイクの権限を許可',
        content:
          'Sokuji がマイクの権限を要求した場合、<strong>「このサイトへのアクセス時に許可」</strong>をクリックしてアクセスを許可します。',
        screenshot: '2.png',
        tip: 'この権限は、Sokuji が翻訳のために音声を処理するのに必要です。',
      },
      {
        title: 'Sokuji 拡張機能を開く',
        content:
          'ブラウザの右上隅にある Sokuji 拡張機能アイコンをクリックし、<strong>「Sokuji を開く」</strong>をクリックしてサイドバーパネルを起動します。',
        screenshot: '3.png',
        tip: 'Sokuji サイドバーはブラウザウィンドウの右側に開きます。',
      },
      {
        title: 'Sokuji 設定を構成',
        content:
          'Sokuji サイドバーで、ソース言語とターゲット言語、AI プロバイダー、その他の設定を含む翻訳設定を構成します。',
        screenshot: '4.png',
        tip: 'マイク入力をテストして、Sokuji が音声を正しく受信していることを確認します。',
      },
      {
        title: 'Sokuji 仮想マイクを選択',
        content:
          'Google Meet のオーディオ設定で、マイクアイコンをクリックし、ドロップダウンメニューから <strong>Sokuji Virtual Microphone</strong> を選択します。',
        screenshot: '5.png',
        tip: '仮想マイクが表示されない場合は、ページを更新して拡張機能がアクティブであることを確認してください。',
      },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        'はっきりと話し、背景ノイズを避ける',
        '翻訳精度を向上させるため、文の間で少し一時停止する',
        '重要な会議の前にオーディオをテストする',
      ],
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          question: 'Sokuji 仮想マイクが表示されないのはなぜですか？',
          answer:
            'Sokuji 拡張機能がインストールされ、アクティブであることを確認してください。Google Meet ページを更新してみてください。問題が解決しない場合は、拡張機能にマイクの権限を付与したかどうかを確認してください。',
        },
        {
          question: '他の参加者は両方の言語を聞くことができますか？',
          answer:
            'デフォルトでは、参加者は翻訳された音声のみを聞きます。Sokuji の「音声」ブロックで「原音パススルー」をオンにすると、参加者には翻訳の下にあなたの元の声も同時に聞こえます。この機能は、リスナーにコンテキストの理解や検証のために両方の言語を聞いてもらいたい場合に便利です。',
        },
        {
          question: 'Sokuji は Google Meet の録画で動作しますか？',
          answer:
            'はい、Sokuji 仮想マイクを使用している場合、翻訳された音声が会議の録画に含まれます。',
        },
        {
          question: '原音パススルーとは何ですか？',
          answer:
            '原音パススルーは、あなた自身の声を小さめの音量で翻訳音声の下に混ぜて仮想マイクに送り、リスナーが両方の言語を同時に聞けるようにする機能です。「音声」ブロック（設定の「クイック」、または 設定 → 詳細 → オーディオ）の「相手に聞こえる翻訳」の下にある項目で、音量スライダーがあります（デフォルト 20%、最大 60%）。「相手に聞こえる翻訳」がオフの間はグレー表示になります。これは、バイリンガルの聴衆や、元の話し声の感情的なトーンやニュアンスを維持したい場合に特に便利です。',
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content:
        '問題が発生した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji">GitHub リポジトリ</a>を訪問するか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までメールでお問い合わせください。',
    },
  },
  ko: {
    pageTitle: 'Google Meet에서 Sokuji 사용하기',
    backLink: '지원 사이트로 돌아가기',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '개요',
      content:
        '이 가이드는 Google Meet에서 Sokuji를 설정하고 사용하여 회의 중 실시간 언어 번역을 수행하는 방법을 안내합니다.',
    },
    steps: [
      {
        title: 'Google Meet 방문',
        content:
          '브라우저를 열고 <code>meet.google.com</code>에 접속합니다. 평소처럼 회의에 참여하거나 시작하세요.',
        screenshot: '1.png',
        tip: 'Chrome 또는 Edge에 Sokuji 확장 프로그램이 설치되고 활성화되어 있는지 확인하세요.',
      },
      {
        title: '마이크 권한 부여',
        content:
          'Sokuji가 마이크 권한을 요청하면 <strong>"이 사이트 방문 시 허용"</strong>을 클릭하여 액세스를 허용합니다.',
        screenshot: '2.png',
        tip: '이 권한은 Sokuji가 번역을 위해 오디오를 처리하는 데 필요합니다.',
      },
      {
        title: 'Sokuji 확장 프로그램 열기',
        content:
          '브라우저 오른쪽 상단의 Sokuji 확장 프로그램 아이콘을 클릭한 다음 <strong>"Sokuji 열기"</strong>를 클릭하여 사이드바 패널을 시작합니다.',
        screenshot: '3.png',
        tip: 'Sokuji 사이드바는 브라우저 창의 오른쪽에 열립니다.',
      },
      {
        title: 'Sokuji 설정 구성',
        content:
          'Sokuji 사이드바에서 소스 언어와 대상 언어, AI 제공업체 및 기타 환경 설정을 포함한 번역 설정을 구성하세요.',
        screenshot: '4.png',
        tip: '마이크 입력을 테스트하여 Sokuji가 오디오를 올바르게 수신하는지 확인하세요.',
      },
      {
        title: 'Sokuji 가상 마이크 선택',
        content:
          'Google Meet의 오디오 설정에서 마이크 아이콘을 클릭하고 드롭다운 메뉴에서 <strong>Sokuji Virtual Microphone</strong>을 선택합니다.',
        screenshot: '5.png',
        tip: '가상 마이크가 보이지 않으면 페이지를 새로 고치고 확장 프로그램이 활성화되어 있는지 확인하세요.',
      },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '명확하게 말하고 배경 소음을 피하세요',
        '번역 정확도 향상을 위해 문장 사이에 잠시 멈추세요',
        '중요한 회의 전에 오디오를 테스트하세요',
      ],
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          question: 'Sokuji 가상 마이크가 보이지 않는 이유는 무엇인가요?',
          answer:
            'Sokuji 확장 프로그램이 설치되고 활성화되어 있는지 확인하세요. Google Meet 페이지를 새로 고쳐 보세요. 문제가 지속되면 확장 프로그램에 마이크 권한을 부여했는지 확인하세요.',
        },
        {
          question: '다른 참가자들이 두 언어를 모두 들을 수 있나요?',
          answer:
            '기본적으로 참가자들은 번역된 오디오만 듣습니다. Sokuji의 \'음성\' 블록에서 \'원음 패스스루\'를 켜면 참가자들이 번역 아래에 깔린 당신의 원래 목소리도 함께 듣습니다. 이 기능은 청취자가 맥락 이해나 검증을 위해 두 언어를 모두 들어야 할 때 유용합니다.',
        },
        {
          question: 'Sokuji가 Google Meet 녹화에서 작동하나요?',
          answer: '네, Sokuji 가상 마이크를 사용하는 경우 번역된 오디오가 회의 녹화에 포함됩니다.',
        },
        {
          question: '원음 패스스루란 무엇인가요?',
          answer:
            '원음 패스스루는 당신의 목소리를 낮은 음량으로 번역 음성 아래에 섞어 가상 마이크로 보내, 청취자가 두 언어를 동시에 들을 수 있게 하는 기능입니다. \'음성\' 블록(설정의 \'빠른\' 화면, 또는 설정 → 고급 → 오디오)에서 \'상대방에게 들리는 번역\' 아래의 하위 항목이며, 음량 슬라이더가 있습니다(기본값 20%, 최대 60%). \'상대방에게 들리는 번역\'이 꺼져 있는 동안에는 회색으로 비활성화됩니다. 이것은 이중 언어 청중이나 원래 말소리의 감정적인 톤과 뉘앙스를 유지하고 싶을 때 특히 유용합니다.',
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content:
        '문제가 발생하면 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 저장소</a>를 방문하거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 지원을 받으세요.',
    },
  },
  es: {
    pageTitle: 'Usar Sokuji con Google Meet',
    backLink: 'Volver a sitios compatibles',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Descripci\u00f3n general',
      content:
        'Esta gu\u00eda le explicar\u00e1 c\u00f3mo configurar y usar Sokuji con Google Meet para la traducci\u00f3n de idiomas en tiempo real durante sus reuniones.',
    },
    steps: [
      {
        title: 'Visite Google Meet',
        content:
          'Abra su navegador y vaya a <code>meet.google.com</code>. \u00danase o inicie una reuni\u00f3n como lo har\u00eda normalmente.',
        screenshot: '1.png',
        tip: 'Aseg\u00farese de tener la extensi\u00f3n Sokuji instalada y habilitada en Chrome o Edge.',
      },
      {
        title: 'Otorgue permiso de micr\u00f3fono',
        content:
          'Si Sokuji solicita permiso de micr\u00f3fono, haga clic en <strong>"Permitir mientras visita el sitio"</strong> para otorgar acceso.',
        screenshot: '2.png',
        tip: 'Este permiso es necesario para que Sokuji procese su audio para la traducci\u00f3n.',
      },
      {
        title: 'Abra la extensi\u00f3n Sokuji',
        content:
          'Haga clic en el icono de la extensi\u00f3n Sokuji en la esquina superior derecha de su navegador, luego haga clic en <strong>"Open Sokuji"</strong> para abrir el panel lateral.',
        screenshot: '3.png',
        tip: 'La barra lateral de Sokuji se abrir\u00e1 en el lado derecho de la ventana de su navegador.',
      },
      {
        title: 'Configure los ajustes de Sokuji',
        content:
          'En la barra lateral de Sokuji, configure sus ajustes de traducci\u00f3n, incluyendo los idiomas de origen y destino, el proveedor de IA y otras preferencias.',
        screenshot: '4.png',
        tip: 'Pruebe la entrada de su micr\u00f3fono para asegurarse de que Sokuji est\u00e9 recibiendo el audio correctamente.',
      },
      {
        title: 'Seleccione Sokuji Virtual Microphone',
        content:
          'En los ajustes de audio de Google Meet, haga clic en el icono del micr\u00f3fono y seleccione <strong>Sokuji Virtual Microphone</strong> en el men\u00fa desplegable.',
        screenshot: '5.png',
        tip: 'Si no ve el micr\u00f3fono virtual, actualice la p\u00e1gina y aseg\u00farese de que la extensi\u00f3n est\u00e9 activa.',
      },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Hable con claridad y evite el ruido de fondo',
        'Haga una breve pausa entre oraciones para mejorar la precisi\u00f3n de la traducci\u00f3n',
        'Pruebe su audio antes de reuniones importantes',
      ],
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '\u00bfPor qu\u00e9 no puedo ver Sokuji Virtual Microphone?',
          answer:
            'Aseg\u00farese de que la extensi\u00f3n Sokuji est\u00e9 instalada y activa. Intente actualizar la p\u00e1gina de Google Meet. Si el problema persiste, verifique que haya otorgado permisos de micr\u00f3fono a la extensi\u00f3n.',
        },
        {
          question: '\u00bfPueden los dem\u00e1s participantes escuchar ambos idiomas?',
          answer:
            'De forma predeterminada, los participantes solo escuchan el audio traducido. Si activa "Paso directo" en el bloque "Voz" de Sokuji, los participantes escucharán su voz original por debajo de la traducción al mismo tiempo. Esta función es útil cuando desea que los oyentes escuchen ambos idiomas para contexto o verificación.',
        },
        {
          question: '\u00bfFunciona Sokuji con las grabaciones de Google Meet?',
          answer:
            'S\u00ed, el audio traducido se incluir\u00e1 en la grabaci\u00f3n de la reuni\u00f3n si est\u00e1 usando Sokuji Virtual Microphone.',
        },
        {
          question: '¿Qué es "Paso directo"?',
          answer:
            '"Paso directo" mezcla su propia voz, a menor volumen, por debajo de la traducción en el micrófono virtual, para que los oyentes escuchen ambos idiomas a la vez. Es una subfila de "Traducción que oye la otra parte" en el bloque "Voz" (ajustes "Rápido", o Configuración → Avanzado → Audio), con un control de volumen: 20 % por defecto, 60 % como máximo. Aparece atenuado mientras "Traducción que oye la otra parte" está desactivada. Esto es particularmente útil para audiencias bilingües o cuando desea mantener el tono emocional y los matices de su discurso original.',
        },
      ],
    },
    troubleshooting: {
      title: 'Soluci\u00f3n de problemas',
      content:
        'Si encuentra alg\u00fan problema, visite nuestro <a href="https://github.com/kizuna-ai-lab/sokuji">repositorio de GitHub</a> o cont\u00e1ctenos en <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obtener ayuda.',
    },
  },
  fr: {
    pageTitle: 'Utiliser Sokuji avec Google Meet',
    backLink: 'Retour aux sites pris en charge',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Aper\u00e7u',
      content:
        'Ce guide vous expliquera comment configurer et utiliser Sokuji avec Google Meet pour la traduction linguistique en temps r\u00e9el lors de vos r\u00e9unions.',
    },
    steps: [
      {
        title: 'Visitez Google Meet',
        content:
          'Ouvrez votre navigateur et acc\u00e9dez \u00e0 <code>meet.google.com</code>. Rejoignez ou d\u00e9marrez une r\u00e9union comme vous le feriez normalement.',
        screenshot: '1.png',
        tip: 'Assurez-vous que l\u2019extension Sokuji est install\u00e9e et activ\u00e9e dans Chrome ou Edge.',
      },
      {
        title: 'Accordez l\u2019autorisation du microphone',
        content:
          'Si Sokuji demande l\u2019autorisation du microphone, cliquez sur <strong>\u00ab Autoriser pendant la visite du site \u00bb</strong> pour accorder l\u2019acc\u00e8s.',
        screenshot: '2.png',
        tip: 'Cette autorisation est n\u00e9cessaire pour que Sokuji traite votre audio pour la traduction.',
      },
      {
        title: 'Ouvrez l\u2019extension Sokuji',
        content:
          'Cliquez sur l\u2019ic\u00f4ne de l\u2019extension Sokuji dans le coin sup\u00e9rieur droit de votre navigateur, puis cliquez sur <strong>\u00ab Open Sokuji \u00bb</strong> pour lancer le panneau lat\u00e9ral.',
        screenshot: '3.png',
        tip: 'La barre lat\u00e9rale Sokuji s\u2019ouvrira sur le c\u00f4t\u00e9 droit de la fen\u00eatre de votre navigateur.',
      },
      {
        title: 'Configurez les param\u00e8tres de Sokuji',
        content:
          'Dans la barre lat\u00e9rale de Sokuji, configurez vos param\u00e8tres de traduction, y compris les langues source et cible, le fournisseur d\u2019IA et d\u2019autres pr\u00e9f\u00e9rences.',
        screenshot: '4.png',
        tip: 'Testez l\u2019entr\u00e9e de votre microphone pour vous assurer que Sokuji re\u00e7oit correctement l\u2019audio.',
      },
      {
        title: 'S\u00e9lectionnez Sokuji Virtual Microphone',
        content:
          'Dans les param\u00e8tres audio de Google Meet, cliquez sur l\u2019ic\u00f4ne du microphone et s\u00e9lectionnez <strong>Sokuji Virtual Microphone</strong> dans le menu d\u00e9roulant.',
        screenshot: '5.png',
        tip: 'Si vous ne voyez pas le microphone virtuel, actualisez la page et assurez-vous que l\u2019extension est active.',
      },
    ],
    tips: {
      title: 'Conseils pour de meilleurs r\u00e9sultats',
      items: [
        'Parlez clairement et \u00e9vitez le bruit de fond',
        'Faites une br\u00e8ve pause entre les phrases pour une meilleure pr\u00e9cision de traduction',
        'Testez votre audio avant les r\u00e9unions importantes',
      ],
    },
    faq: {
      title: 'Questions fr\u00e9quemment pos\u00e9es',
      items: [
        {
          question: 'Pourquoi ne puis-je pas voir Sokuji Virtual Microphone\u00a0?',
          answer:
            'Assurez-vous que l\u2019extension Sokuji est install\u00e9e et active. Essayez d\u2019actualiser la page Google Meet. Si le probl\u00e8me persiste, v\u00e9rifiez que vous avez accord\u00e9 les autorisations de microphone \u00e0 l\u2019extension.',
        },
        {
          question: 'Les autres participants peuvent-ils entendre les deux langues\u00a0?',
          answer:
            'Par défaut, les participants n’entendent que l’audio traduit. Si vous activez « Passage direct » dans le bloc « Voix » de Sokuji, les participants entendent votre voix originale sous la traduction, en même temps. Cette fonctionnalité est utile lorsque vous souhaitez que les auditeurs entendent les deux langues pour le contexte ou la vérification.',
        },
        {
          question: 'Sokuji fonctionne-t-il avec les enregistrements Google Meet\u00a0?',
          answer:
            'Oui, l\u2019audio traduit sera inclus dans l\u2019enregistrement de la r\u00e9union si vous utilisez Sokuji Virtual Microphone.',
        },
        {
          question: 'Qu’est-ce que le « Passage direct » ?',
          answer:
            'Le « Passage direct » mélange votre propre voix, à un volume plus bas, sous la traduction dans le micro virtuel, pour que les auditeurs entendent les deux langues à la fois. C’est une sous-ligne de « Traduction entendue par l\'autre » dans le bloc « Voix » (paramètres « Rapide », ou Paramètres → Avancé → Audio), avec un curseur de volume : 20 % par défaut, 60 % au maximum. Il est grisé tant que « Traduction entendue par l\'autre » est désactivée. C’est particulièrement utile pour les audiences bilingues ou lorsque vous souhaitez conserver le ton émotionnel et les nuances de votre discours original.',
        },
      ],
    },
    troubleshooting: {
      title: 'D\u00e9pannage',
      content:
        'Si vous rencontrez des probl\u00e8mes, veuillez visiter notre <a href="https://github.com/kizuna-ai-lab/sokuji">d\u00e9p\u00f4t GitHub</a> ou contactez-nous \u00e0 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> pour obtenir de l\u2019aide.',
    },
  },
  ar: {
    pageTitle: '\u0627\u0633\u062a\u062e\u062f\u0627\u0645 Sokuji \u0645\u0639 Google Meet',
    backLink: '\u0627\u0644\u0639\u0648\u062f\u0629 \u0625\u0644\u0649 \u0627\u0644\u0645\u0648\u0627\u0642\u0639 \u0627\u0644\u0645\u062f\u0639\u0648\u0645\u0629',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629',
      content:
        '\u0633\u064a\u0631\u0634\u062f\u0643 \u0647\u0630\u0627 \u0627\u0644\u062f\u0644\u064a\u0644 \u062e\u0644\u0627\u0644 \u0625\u0639\u062f\u0627\u062f \u0648\u0627\u0633\u062a\u062e\u062f\u0627\u0645 Sokuji \u0645\u0639 Google Meet \u0644\u0644\u062a\u0631\u062c\u0645\u0629 \u0627\u0644\u0641\u0648\u0631\u064a\u0629 \u0623\u062b\u0646\u0627\u0621 \u0627\u062c\u062a\u0645\u0627\u0639\u0627\u062a\u0643.',
    },
    steps: [
      {
        title: '\u0642\u0645 \u0628\u0632\u064a\u0627\u0631\u0629 Google Meet',
        content:
          '\u0627\u0641\u062a\u062d \u0645\u062a\u0635\u0641\u062d\u0643 \u0648\u0627\u0646\u062a\u0642\u0644 \u0625\u0644\u0649 <code>meet.google.com</code>. \u0627\u0646\u0636\u0645 \u0625\u0644\u0649 \u0627\u062c\u062a\u0645\u0627\u0639 \u0623\u0648 \u0627\u0628\u062f\u0623 \u0627\u062c\u062a\u0645\u0627\u0639\u064b\u0627 \u0643\u0627\u0644\u0645\u0639\u062a\u0627\u062f.',
        screenshot: '1.png',
        tip: '\u062a\u0623\u0643\u062f \u0645\u0646 \u0623\u0646 \u0625\u0636\u0627\u0641\u0629 Sokuji \u0645\u062b\u0628\u062a\u0629 \u0648\u0645\u0641\u0639\u0644\u0629 \u0641\u064a Chrome \u0623\u0648 Edge.',
      },
      {
        title: '\u0627\u0645\u0646\u062d \u0625\u0630\u0646 \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646',
        content:
          '\u0625\u0630\u0627 \u0637\u0644\u0628 Sokuji \u0625\u0630\u0646 \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646\u060c \u0627\u0646\u0642\u0631 \u0639\u0644\u0649 <strong>"\u0627\u0644\u0633\u0645\u0627\u062d \u0623\u062b\u0646\u0627\u0621 \u0632\u064a\u0627\u0631\u0629 \u0627\u0644\u0645\u0648\u0642\u0639"</strong> \u0644\u0645\u0646\u062d \u0627\u0644\u0648\u0635\u0648\u0644.',
        screenshot: '2.png',
        tip: '\u0647\u0630\u0627 \u0627\u0644\u0625\u0630\u0646 \u0645\u0637\u0644\u0648\u0628 \u0644\u0643\u064a \u064a\u062a\u0645\u0643\u0646 Sokuji \u0645\u0646 \u0645\u0639\u0627\u0644\u062c\u0629 \u0627\u0644\u0635\u0648\u062a \u0644\u0644\u062a\u0631\u062c\u0645\u0629.',
      },
      {
        title: '\u0627\u0641\u062a\u062d \u0625\u0636\u0627\u0641\u0629 Sokuji',
        content:
          '\u0627\u0646\u0642\u0631 \u0639\u0644\u0649 \u0623\u064a\u0642\u0648\u0646\u0629 \u0625\u0636\u0627\u0641\u0629 Sokuji \u0641\u064a \u0627\u0644\u0632\u0627\u0648\u064a\u0629 \u0627\u0644\u0639\u0644\u0648\u064a\u0629 \u0627\u0644\u064a\u0645\u0646\u0649 \u0645\u0646 \u0645\u062a\u0635\u0641\u062d\u0643\u060c \u062b\u0645 \u0627\u0646\u0642\u0631 \u0639\u0644\u0649 <strong>"Open Sokuji"</strong> \u0644\u0641\u062a\u062d \u0627\u0644\u0644\u0648\u062d\u0629 \u0627\u0644\u062c\u0627\u0646\u0628\u064a\u0629.',
        screenshot: '3.png',
        tip: '\u0633\u064a\u062a\u0645 \u0641\u062a\u062d \u0627\u0644\u0634\u0631\u064a\u0637 \u0627\u0644\u062c\u0627\u0646\u0628\u064a \u0644\u0640 Sokuji \u0639\u0644\u0649 \u0627\u0644\u062c\u0627\u0646\u0628 \u0627\u0644\u0623\u064a\u0645\u0646 \u0645\u0646 \u0646\u0627\u0641\u0630\u0629 \u0627\u0644\u0645\u062a\u0635\u0641\u062d.',
      },
      {
        title: '\u0642\u0645 \u0628\u062a\u0643\u0648\u064a\u0646 \u0625\u0639\u062f\u0627\u062f\u0627\u062a Sokuji',
        content:
          '\u0641\u064a \u0627\u0644\u0634\u0631\u064a\u0637 \u0627\u0644\u062c\u0627\u0646\u0628\u064a \u0644\u0640 Sokuji\u060c \u0642\u0645 \u0628\u062a\u0643\u0648\u064a\u0646 \u0625\u0639\u062f\u0627\u062f\u0627\u062a \u0627\u0644\u062a\u0631\u062c\u0645\u0629 \u0628\u0645\u0627 \u0641\u064a \u0630\u0644\u0643 \u0644\u063a\u0627\u062a \u0627\u0644\u0645\u0635\u062f\u0631 \u0648\u0627\u0644\u0647\u062f\u0641 \u0648\u0645\u0632\u0648\u062f \u0627\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064a \u0648\u0627\u0644\u062a\u0641\u0636\u064a\u0644\u0627\u062a \u0627\u0644\u0623\u062e\u0631\u0649.',
        screenshot: '4.png',
        tip: '\u0627\u062e\u062a\u0628\u0631 \u0625\u062f\u062e\u0627\u0644 \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0644\u0644\u062a\u0623\u0643\u062f \u0645\u0646 \u0623\u0646 Sokuji \u064a\u0633\u062a\u0642\u0628\u0644 \u0627\u0644\u0635\u0648\u062a \u0628\u0634\u0643\u0644 \u0635\u062d\u064a\u062d.',
      },
      {
        title: '\u062d\u062f\u062f Sokuji Virtual Microphone',
        content:
          '\u0641\u064a \u0625\u0639\u062f\u0627\u062f\u0627\u062a \u0627\u0644\u0635\u0648\u062a \u0641\u064a Google Meet\u060c \u0627\u0646\u0642\u0631 \u0639\u0644\u0649 \u0623\u064a\u0642\u0648\u0646\u0629 \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0648\u062d\u062f\u062f <strong>Sokuji Virtual Microphone</strong> \u0645\u0646 \u0627\u0644\u0642\u0627\u0626\u0645\u0629 \u0627\u0644\u0645\u0646\u0633\u062f\u0644\u0629.',
        screenshot: '5.png',
        tip: '\u0625\u0630\u0627 \u0644\u0645 \u062a\u0631\u064e \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0627\u0644\u0627\u0641\u062a\u0631\u0627\u0636\u064a\u060c \u0642\u0645 \u0628\u062a\u062d\u062f\u064a\u062b \u0627\u0644\u0635\u0641\u062d\u0629 \u0648\u062a\u0623\u0643\u062f \u0645\u0646 \u0623\u0646 \u0627\u0644\u0625\u0636\u0627\u0641\u0629 \u0646\u0634\u0637\u0629.',
      },
    ],
    tips: {
      title: '\u0646\u0635\u0627\u0626\u062d \u0644\u0644\u062d\u0635\u0648\u0644 \u0639\u0644\u0649 \u0623\u0641\u0636\u0644 \u0627\u0644\u0646\u062a\u0627\u0626\u062c',
      items: [
        '\u062a\u062d\u062f\u062b \u0628\u0648\u0636\u0648\u062d \u0648\u062a\u062c\u0646\u0628 \u0627\u0644\u0636\u0648\u0636\u0627\u0621 \u0627\u0644\u062e\u0644\u0641\u064a\u0629',
        '\u062a\u0648\u0642\u0641 \u0644\u0641\u062a\u0631\u0629 \u0648\u062c\u064a\u0632\u0629 \u0628\u064a\u0646 \u0627\u0644\u062c\u0645\u0644 \u0644\u062a\u062d\u0633\u064a\u0646 \u062f\u0642\u0629 \u0627\u0644\u062a\u0631\u062c\u0645\u0629',
        '\u0627\u062e\u062a\u0628\u0631 \u0627\u0644\u0635\u0648\u062a \u0642\u0628\u0644 \u0627\u0644\u0627\u062c\u062a\u0645\u0627\u0639\u0627\u062a \u0627\u0644\u0645\u0647\u0645\u0629',
      ],
    },
    faq: {
      title: '\u0627\u0644\u0623\u0633\u0626\u0644\u0629 \u0627\u0644\u0634\u0627\u0626\u0639\u0629',
      items: [
        {
          question: '\u0644\u0645\u0627\u0630\u0627 \u0644\u0627 \u0623\u0633\u062a\u0637\u064a\u0639 \u0631\u0624\u064a\u0629 Sokuji Virtual Microphone\u061f',
          answer:
            '\u062a\u0623\u0643\u062f \u0645\u0646 \u0623\u0646 \u0625\u0636\u0627\u0641\u0629 Sokuji \u0645\u062b\u0628\u062a\u0629 \u0648\u0646\u0634\u0637\u0629. \u062d\u0627\u0648\u0644 \u062a\u062d\u062f\u064a\u062b \u0635\u0641\u062d\u0629 Google Meet. \u0625\u0630\u0627 \u0627\u0633\u062a\u0645\u0631\u062a \u0627\u0644\u0645\u0634\u0643\u0644\u0629\u060c \u062a\u062d\u0642\u0642 \u0645\u0646 \u0623\u0646\u0643 \u0645\u0646\u062d\u062a \u0623\u0630\u0648\u0646\u0627\u062a \u0627\u0644\u0645\u064a\u0643\u0631\u0648\u0641\u0648\u0646 \u0644\u0644\u0625\u0636\u0627\u0641\u0629.',
        },
        {
          question: '\u0647\u0644 \u064a\u0645\u0643\u0646 \u0644\u0644\u0645\u0634\u0627\u0631\u0643\u064a\u0646 \u0627\u0644\u0622\u062e\u0631\u064a\u0646 \u0633\u0645\u0627\u0639 \u0643\u0644\u062a\u0627 \u0627\u0644\u0644\u063a\u062a\u064a\u0646\u061f',
          answer:
            'بشكل افتراضي، يسمع المشاركون الصوت المترجم فقط. إذا فعّلت "التمرير المباشر" في قسم "الكلام" في Sokuji، فسيسمع المشاركون صوتك الأصلي تحت الترجمة في آن واحد. هذه الميزة مفيدة عندما تريد أن يسمع المستمعون كلتا اللغتين للسياق أو التحقق.',
        },
        {
          question: '\u0647\u0644 \u064a\u0639\u0645\u0644 Sokuji \u0645\u0639 \u062a\u0633\u062c\u064a\u0644\u0627\u062a Google Meet\u061f',
          answer:
            '\u0646\u0639\u0645\u060c \u0633\u064a\u062a\u0645 \u062a\u0636\u0645\u064a\u0646 \u0627\u0644\u0635\u0648\u062a \u0627\u0644\u0645\u062a\u0631\u062c\u0645 \u0641\u064a \u062a\u0633\u062c\u064a\u0644 \u0627\u0644\u0627\u062c\u062a\u0645\u0627\u0639 \u0625\u0630\u0627 \u0643\u0646\u062a \u062a\u0633\u062a\u062e\u062f\u0645 Sokuji Virtual Microphone.',
        },
        {
          question: 'ما هو "التمرير المباشر"؟',
          answer:
            '"التمرير المباشر" يمزج صوتك بمستوى أخفض تحت الترجمة في الميكروفون الافتراضي، فيسمع المستمعون كلتا اللغتين في آن واحد. وهو صف فرعي تحت "الترجمة التي يسمعها الطرف الآخر" في قسم "الكلام" (وضع "سريع" في الإعدادات، أو الإعدادات ← متقدم ← الصوت)، وله شريط لمستوى الصوت: 20% افتراضيًا، و60% كحد أقصى. ويظهر باهتًا ما دامت "الترجمة التي يسمعها الطرف الآخر" متوقفة. هذا مفيد بشكل خاص للجماهير ثنائية اللغة أو عندما تريد الحفاظ على النبرة العاطفية والفروق الدقيقة في خطابك الأصلي.',
        },
      ],
    },
    troubleshooting: {
      title: '\u0627\u0633\u062a\u0643\u0634\u0627\u0641 \u0627\u0644\u0623\u062e\u0637\u0627\u0621 \u0648\u0625\u0635\u0644\u0627\u062d\u0647\u0627',
      content:
        '\u0625\u0630\u0627 \u0648\u0627\u062c\u0647\u062a \u0623\u064a \u0645\u0634\u0627\u0643\u0644\u060c \u064a\u0631\u062c\u0649 \u0632\u064a\u0627\u0631\u0629 <a href="https://github.com/kizuna-ai-lab/sokuji">\u0645\u0633\u062a\u0648\u062f\u0639 GitHub</a> \u0627\u0644\u062e\u0627\u0635 \u0628\u0646\u0627 \u0623\u0648 \u0627\u0644\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627 \u0639\u0628\u0631 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> \u0644\u0644\u062d\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u062f\u0639\u0645.',
    },
  },
  pt: {
    pageTitle: 'Usando o Sokuji com o Google Meet',
    backLink: 'Voltar para sites suportados',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Vis\u00e3o geral',
      content:
        'Este guia ir\u00e1 orient\u00e1-lo na configura\u00e7\u00e3o e uso do Sokuji com o Google Meet para tradu\u00e7\u00e3o de idiomas em tempo real durante suas reuni\u00f5es.',
    },
    steps: [
      {
        title: 'Visite o Google Meet',
        content:
          'Abra seu navegador e acesse <code>meet.google.com</code>. Participe ou inicie uma reuni\u00e3o como faria normalmente.',
        screenshot: '1.png',
        tip: 'Certifique-se de que a extens\u00e3o Sokuji esteja instalada e ativada no Chrome ou Edge.',
      },
      {
        title: 'Conceda permiss\u00e3o do microfone',
        content:
          'Se o Sokuji solicitar permiss\u00e3o do microfone, clique em <strong>"Permitir enquanto visita o site"</strong> para conceder acesso.',
        screenshot: '2.png',
        tip: 'Esta permiss\u00e3o \u00e9 necess\u00e1ria para que o Sokuji processe seu \u00e1udio para tradu\u00e7\u00e3o.',
      },
      {
        title: 'Abra a extens\u00e3o Sokuji',
        content:
          'Clique no \u00edcone da extens\u00e3o Sokuji no canto superior direito do seu navegador e clique em <strong>"Open Sokuji"</strong> para abrir o painel lateral.',
        screenshot: '3.png',
        tip: 'A barra lateral do Sokuji ser\u00e1 aberta no lado direito da janela do navegador.',
      },
      {
        title: 'Configure as defini\u00e7\u00f5es do Sokuji',
        content:
          'Na barra lateral do Sokuji, configure suas defini\u00e7\u00f5es de tradu\u00e7\u00e3o, incluindo os idiomas de origem e destino, o provedor de IA e outras prefer\u00eancias.',
        screenshot: '4.png',
        tip: 'Teste a entrada do microfone para garantir que o Sokuji esteja recebendo o \u00e1udio corretamente.',
      },
      {
        title: 'Selecione o Sokuji Virtual Microphone',
        content:
          'Nas configura\u00e7\u00f5es de \u00e1udio do Google Meet, clique no \u00edcone do microfone e selecione <strong>Sokuji Virtual Microphone</strong> no menu suspenso.',
        screenshot: '5.png',
        tip: 'Se voc\u00ea n\u00e3o vir o microfone virtual, atualize a p\u00e1gina e certifique-se de que a extens\u00e3o esteja ativa.',
      },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Fale com clareza e evite ru\u00eddo de fundo',
        'Fa\u00e7a uma breve pausa entre as frases para melhorar a precis\u00e3o da tradu\u00e7\u00e3o',
        'Teste seu \u00e1udio antes de reuni\u00f5es importantes',
      ],
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'Por que n\u00e3o consigo ver o Sokuji Virtual Microphone?',
          answer:
            'Certifique-se de que a extens\u00e3o Sokuji esteja instalada e ativa. Tente atualizar a p\u00e1gina do Google Meet. Se o problema persistir, verifique se voc\u00ea concedeu permiss\u00f5es de microfone \u00e0 extens\u00e3o.',
        },
        {
          question: 'Os outros participantes podem ouvir ambos os idiomas?',
          answer:
            'Por padrão, os participantes ouvem apenas o áudio traduzido. Se você ativar "Passagem direta" no bloco "Voz" do Sokuji, os participantes ouvirão sua voz original por baixo da tradução ao mesmo tempo. Este recurso é útil quando você deseja que os ouvintes ouçam ambos os idiomas para contexto ou verificação.',
        },
        {
          question: 'O Sokuji funciona com as grava\u00e7\u00f5es do Google Meet?',
          answer:
            'Sim, o \u00e1udio traduzido ser\u00e1 inclu\u00eddo na grava\u00e7\u00e3o da reuni\u00e3o se voc\u00ea estiver usando o Sokuji Virtual Microphone.',
        },
        {
          question: 'O que é a "Passagem direta"?',
          answer:
            'A "Passagem direta" mistura sua própria voz, em volume mais baixo, por baixo da tradução no microfone virtual, para que os ouvintes ouçam os dois idiomas ao mesmo tempo. Ela é uma subopção de "Tradução que o outro lado ouve" no bloco "Voz" (configurações "Rápido", ou Configurações → Avançado → Áudio), com um controle de volume: 20% por padrão, no máximo 60%. Fica esmaecida enquanto "Tradução que o outro lado ouve" está desligada. Isso é particularmente útil para audiências bilíngues ou quando você deseja manter o tom emocional e as nuances do seu discurso original.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solu\u00e7\u00e3o de problemas',
      content:
        'Se voc\u00ea encontrar algum problema, visite nosso <a href="https://github.com/kizuna-ai-lab/sokuji">reposit\u00f3rio GitHub</a> ou entre em contato conosco em <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obter suporte.',
    },
  },
  ru: {
    pageTitle: '\u0418\u0441\u043f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u043d\u0438\u0435 Sokuji \u0441 Google Meet',
    backLink: '\u041d\u0430\u0437\u0430\u0434 \u043a \u043f\u043e\u0434\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u043c\u044b\u043c \u0441\u0430\u0439\u0442\u0430\u043c',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u041e\u0431\u0437\u043e\u0440',
      content:
        '\u042d\u0442\u043e \u0440\u0443\u043a\u043e\u0432\u043e\u0434\u0441\u0442\u0432\u043e \u043f\u043e\u043c\u043e\u0436\u0435\u0442 \u0432\u0430\u043c \u043d\u0430\u0441\u0442\u0440\u043e\u0438\u0442\u044c \u0438 \u0438\u0441\u043f\u043e\u043b\u044c\u0437\u043e\u0432\u0430\u0442\u044c Sokuji \u0441 Google Meet \u0434\u043b\u044f \u043f\u0435\u0440\u0435\u0432\u043e\u0434\u0430 \u0432 \u0440\u0435\u0430\u043b\u044c\u043d\u043e\u043c \u0432\u0440\u0435\u043c\u0435\u043d\u0438 \u0432\u043e \u0432\u0440\u0435\u043c\u044f \u0432\u0430\u0448\u0438\u0445 \u0432\u0441\u0442\u0440\u0435\u0447.',
    },
    steps: [
      {
        title: '\u041f\u043e\u0441\u0435\u0442\u0438\u0442\u0435 Google Meet',
        content:
          '\u041e\u0442\u043a\u0440\u043e\u0439\u0442\u0435 \u0431\u0440\u0430\u0443\u0437\u0435\u0440 \u0438 \u043f\u0435\u0440\u0435\u0439\u0434\u0438\u0442\u0435 \u043d\u0430 <code>meet.google.com</code>. \u041f\u0440\u0438\u0441\u043e\u0435\u0434\u0438\u043d\u0438\u0442\u0435\u0441\u044c \u043a \u0432\u0441\u0442\u0440\u0435\u0447\u0435 \u0438\u043b\u0438 \u0441\u043e\u0437\u0434\u0430\u0439\u0442\u0435 \u043d\u043e\u0432\u0443\u044e, \u043a\u0430\u043a \u043e\u0431\u044b\u0447\u043d\u043e.',
        screenshot: '1.png',
        tip: '\u0423\u0431\u0435\u0434\u0438\u0442\u0435\u0441\u044c, \u0447\u0442\u043e \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u0435 Sokuji \u0443\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0438 \u0432\u043a\u043b\u044e\u0447\u0435\u043d\u043e \u0432 Chrome \u0438\u043b\u0438 Edge.',
      },
      {
        title: '\u041f\u0440\u0435\u0434\u043e\u0441\u0442\u0430\u0432\u044c\u0442\u0435 \u0440\u0430\u0437\u0440\u0435\u0448\u0435\u043d\u0438\u0435 \u043d\u0430 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d',
        content:
          '\u0415\u0441\u043b\u0438 Sokuji \u0437\u0430\u043f\u0440\u0430\u0448\u0438\u0432\u0430\u0435\u0442 \u0440\u0430\u0437\u0440\u0435\u0448\u0435\u043d\u0438\u0435 \u043d\u0430 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d, \u043d\u0430\u0436\u043c\u0438\u0442\u0435 <strong>\u00ab\u0420\u0430\u0437\u0440\u0435\u0448\u0438\u0442\u044c \u043f\u0440\u0438 \u043f\u043e\u0441\u0435\u0449\u0435\u043d\u0438\u0438 \u0441\u0430\u0439\u0442\u0430\u00bb</strong>, \u0447\u0442\u043e\u0431\u044b \u043f\u0440\u0435\u0434\u043e\u0441\u0442\u0430\u0432\u0438\u0442\u044c \u0434\u043e\u0441\u0442\u0443\u043f.',
        screenshot: '2.png',
        tip: '\u042d\u0442\u043e \u0440\u0430\u0437\u0440\u0435\u0448\u0435\u043d\u0438\u0435 \u043d\u0435\u043e\u0431\u0445\u043e\u0434\u0438\u043c\u043e \u0434\u043b\u044f \u043e\u0431\u0440\u0430\u0431\u043e\u0442\u043a\u0438 \u0430\u0443\u0434\u0438\u043e Sokuji \u0434\u043b\u044f \u043f\u0435\u0440\u0435\u0432\u043e\u0434\u0430.',
      },
      {
        title: '\u041e\u0442\u043a\u0440\u043e\u0439\u0442\u0435 \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u0435 Sokuji',
        content:
          '\u041d\u0430\u0436\u043c\u0438\u0442\u0435 \u043d\u0430 \u0437\u043d\u0430\u0447\u043e\u043a \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u044f Sokuji \u0432 \u043f\u0440\u0430\u0432\u043e\u043c \u0432\u0435\u0440\u0445\u043d\u0435\u043c \u0443\u0433\u043b\u0443 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430, \u0437\u0430\u0442\u0435\u043c \u043d\u0430\u0436\u043c\u0438\u0442\u0435 <strong>\u00abOpen Sokuji\u00bb</strong>, \u0447\u0442\u043e\u0431\u044b \u043e\u0442\u043a\u0440\u044b\u0442\u044c \u0431\u043e\u043a\u043e\u0432\u0443\u044e \u043f\u0430\u043d\u0435\u043b\u044c.',
        screenshot: '3.png',
        tip: '\u0411\u043e\u043a\u043e\u0432\u0430\u044f \u043f\u0430\u043d\u0435\u043b\u044c Sokuji \u043e\u0442\u043a\u0440\u043e\u0435\u0442\u0441\u044f \u0432 \u043f\u0440\u0430\u0432\u043e\u0439 \u0447\u0430\u0441\u0442\u0438 \u043e\u043a\u043d\u0430 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430.',
      },
      {
        title: '\u041d\u0430\u0441\u0442\u0440\u043e\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u044b Sokuji',
        content:
          '\u0412 \u0431\u043e\u043a\u043e\u0432\u043e\u0439 \u043f\u0430\u043d\u0435\u043b\u0438 Sokuji \u043d\u0430\u0441\u0442\u0440\u043e\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u044b \u043f\u0435\u0440\u0435\u0432\u043e\u0434\u0430, \u0432\u043a\u043b\u044e\u0447\u0430\u044f \u0438\u0441\u0445\u043e\u0434\u043d\u044b\u0439 \u0438 \u0446\u0435\u043b\u0435\u0432\u043e\u0439 \u044f\u0437\u044b\u043a\u0438, \u043f\u043e\u0441\u0442\u0430\u0432\u0449\u0438\u043a\u0430 \u0418\u0418 \u0438 \u0434\u0440\u0443\u0433\u0438\u0435 \u043f\u0440\u0435\u0434\u043f\u043e\u0447\u0442\u0435\u043d\u0438\u044f.',
        screenshot: '4.png',
        tip: '\u041f\u0440\u043e\u0442\u0435\u0441\u0442\u0438\u0440\u0443\u0439\u0442\u0435 \u0432\u0445\u043e\u0434 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d\u0430, \u0447\u0442\u043e\u0431\u044b \u0443\u0431\u0435\u0434\u0438\u0442\u044c\u0441\u044f, \u0447\u0442\u043e Sokuji \u043f\u0440\u0430\u0432\u0438\u043b\u044c\u043d\u043e \u043f\u0440\u0438\u043d\u0438\u043c\u0430\u0435\u0442 \u0430\u0443\u0434\u0438\u043e.',
      },
      {
        title: '\u0412\u044b\u0431\u0435\u0440\u0438\u0442\u0435 Sokuji Virtual Microphone',
        content:
          '\u0412 \u043d\u0430\u0441\u0442\u0440\u043e\u0439\u043a\u0430\u0445 \u0430\u0443\u0434\u0438\u043e Google Meet \u043d\u0430\u0436\u043c\u0438\u0442\u0435 \u043d\u0430 \u0437\u043d\u0430\u0447\u043e\u043a \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d\u0430 \u0438 \u0432\u044b\u0431\u0435\u0440\u0438\u0442\u0435 <strong>Sokuji Virtual Microphone</strong> \u0438\u0437 \u0432\u044b\u043f\u0430\u0434\u0430\u044e\u0449\u0435\u0433\u043e \u043c\u0435\u043d\u044e.',
        screenshot: '5.png',
        tip: '\u0415\u0441\u043b\u0438 \u0432\u044b \u043d\u0435 \u0432\u0438\u0434\u0438\u0442\u0435 \u0432\u0438\u0440\u0442\u0443\u0430\u043b\u044c\u043d\u044b\u0439 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d, \u043e\u0431\u043d\u043e\u0432\u0438\u0442\u0435 \u0441\u0442\u0440\u0430\u043d\u0438\u0446\u0443 \u0438 \u0443\u0431\u0435\u0434\u0438\u0442\u0435\u0441\u044c, \u0447\u0442\u043e \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u0435 \u0430\u043a\u0442\u0438\u0432\u043d\u043e.',
      },
    ],
    tips: {
      title: '\u0421\u043e\u0432\u0435\u0442\u044b \u0434\u043b\u044f \u043b\u0443\u0447\u0448\u0438\u0445 \u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u043e\u0432',
      items: [
        '\u0413\u043e\u0432\u043e\u0440\u0438\u0442\u0435 \u0447\u0435\u0442\u043a\u043e \u0438 \u0438\u0437\u0431\u0435\u0433\u0430\u0439\u0442\u0435 \u0444\u043e\u043d\u043e\u0432\u043e\u0433\u043e \u0448\u0443\u043c\u0430',
        '\u0414\u0435\u043b\u0430\u0439\u0442\u0435 \u043a\u043e\u0440\u043e\u0442\u043a\u0438\u0435 \u043f\u0430\u0443\u0437\u044b \u043c\u0435\u0436\u0434\u0443 \u043f\u0440\u0435\u0434\u043b\u043e\u0436\u0435\u043d\u0438\u044f\u043c\u0438 \u0434\u043b\u044f \u043b\u0443\u0447\u0448\u0435\u0439 \u0442\u043e\u0447\u043d\u043e\u0441\u0442\u0438 \u043f\u0435\u0440\u0435\u0432\u043e\u0434\u0430',
        '\u041f\u0440\u043e\u0442\u0435\u0441\u0442\u0438\u0440\u0443\u0439\u0442\u0435 \u0430\u0443\u0434\u0438\u043e \u043f\u0435\u0440\u0435\u0434 \u0432\u0430\u0436\u043d\u044b\u043c\u0438 \u0432\u0441\u0442\u0440\u0435\u0447\u0430\u043c\u0438',
      ],
    },
    faq: {
      title: '\u0427\u0430\u0441\u0442\u043e \u0437\u0430\u0434\u0430\u0432\u0430\u0435\u043c\u044b\u0435 \u0432\u043e\u043f\u0440\u043e\u0441\u044b',
      items: [
        {
          question: '\u041f\u043e\u0447\u0435\u043c\u0443 \u044f \u043d\u0435 \u0432\u0438\u0436\u0443 Sokuji Virtual Microphone?',
          answer:
            '\u0423\u0431\u0435\u0434\u0438\u0442\u0435\u0441\u044c, \u0447\u0442\u043e \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u0435 Sokuji \u0443\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0438 \u0430\u043a\u0442\u0438\u0432\u043d\u043e. \u041f\u043e\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u043e\u0431\u043d\u043e\u0432\u0438\u0442\u044c \u0441\u0442\u0440\u0430\u043d\u0438\u0446\u0443 Google Meet. \u0415\u0441\u043b\u0438 \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u0430 \u043d\u0435 \u0438\u0441\u0447\u0435\u0437\u043d\u0435\u0442, \u043f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435, \u0447\u0442\u043e \u0432\u044b \u043f\u0440\u0435\u0434\u043e\u0441\u0442\u0430\u0432\u0438\u043b\u0438 \u0440\u0430\u0437\u0440\u0435\u0448\u0435\u043d\u0438\u044f \u043d\u0430 \u043c\u0438\u043a\u0440\u043e\u0444\u043e\u043d \u0440\u0430\u0441\u0448\u0438\u0440\u0435\u043d\u0438\u044e.',
        },
        {
          question: '\u041c\u043e\u0433\u0443\u0442 \u043b\u0438 \u0434\u0440\u0443\u0433\u0438\u0435 \u0443\u0447\u0430\u0441\u0442\u043d\u0438\u043a\u0438 \u0441\u043b\u044b\u0448\u0430\u0442\u044c \u043e\u0431\u0430 \u044f\u0437\u044b\u043a\u0430?',
          answer:
            'По умолчанию участники слышат только переведённое аудио. Если включить «Passthrough» в блоке «Речь» в Sokuji, участники одновременно услышат и ваш оригинальный голос под переводом. Эта функция полезна, когда вы хотите, чтобы слушатели слышали оба языка для контекста или проверки.',
        },
        {
          question: '\u0420\u0430\u0431\u043e\u0442\u0430\u0435\u0442 \u043b\u0438 Sokuji \u0441 \u0437\u0430\u043f\u0438\u0441\u044f\u043c\u0438 Google Meet?',
          answer:
            '\u0414\u0430, \u043f\u0435\u0440\u0435\u0432\u0435\u0434\u0435\u043d\u043d\u043e\u0435 \u0430\u0443\u0434\u0438\u043e \u0431\u0443\u0434\u0435\u0442 \u0432\u043a\u043b\u044e\u0447\u0435\u043d\u043e \u0432 \u0437\u0430\u043f\u0438\u0441\u044c \u0432\u0441\u0442\u0440\u0435\u0447\u0438, \u0435\u0441\u043b\u0438 \u0432\u044b \u0438\u0441\u043f\u043e\u043b\u044c\u0437\u0443\u0435\u0442\u0435 Sokuji Virtual Microphone.',
        },
        {
          question: 'Что такое Passthrough?',
          answer:
            'Passthrough подмешивает ваш собственный голос тише под перевод в виртуальный микрофон, чтобы слушатели слышали оба языка одновременно. Это подпункт строки «Перевод, который слышит собеседник» в блоке «Речь» (режим «Быстрый» в настройках или Настройки → Расширенный → Аудио), с ползунком громкости: 20% по умолчанию, не более 60%. Пока «Перевод, который слышит собеседник» выключен, он неактивен (серый). Это особенно полезно для двуязычной аудитории или когда вы хотите сохранить эмоциональный тон и нюансы оригинальной речи.',
        },
      ],
    },
    troubleshooting: {
      title: '\u0423\u0441\u0442\u0440\u0430\u043d\u0435\u043d\u0438\u0435 \u043d\u0435\u043f\u043e\u043b\u0430\u0434\u043e\u043a',
      content:
        '\u0415\u0441\u043b\u0438 \u0443 \u0432\u0430\u0441 \u0432\u043e\u0437\u043d\u0438\u043a\u043b\u0438 \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u044b, \u043f\u043e\u0441\u0435\u0442\u0438\u0442\u0435 \u043d\u0430\u0448 <a href="https://github.com/kizuna-ai-lab/sokuji">\u0440\u0435\u043f\u043e\u0437\u0438\u0442\u043e\u0440\u0438\u0439 GitHub</a> \u0438\u043b\u0438 \u0441\u0432\u044f\u0436\u0438\u0442\u0435\u0441\u044c \u0441 \u043d\u0430\u043c\u0438 \u043f\u043e \u0430\u0434\u0440\u0435\u0441\u0443 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> \u0434\u043b\u044f \u043f\u043e\u043b\u0443\u0447\u0435\u043d\u0438\u044f \u043f\u043e\u0434\u0434\u0435\u0440\u0436\u043a\u0438.',
    },
  },
  de: {
    pageTitle: 'Sokuji mit Google Meet verwenden',
    backLink: 'Zur\u00fcck zu unterst\u00fctzten Websites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u00dcbersicht',
      content:
        'Diese Anleitung f\u00fchrt Sie durch die Einrichtung und Verwendung von Sokuji mit Google Meet f\u00fcr Echtzeit-Sprach\u00fcbersetzung w\u00e4hrend Ihrer Besprechungen.',
    },
    steps: [
      {
        title: 'Besuchen Sie Google Meet',
        content:
          '\u00d6ffnen Sie Ihren Browser und navigieren Sie zu <code>meet.google.com</code>. Treten Sie einer Besprechung bei oder starten Sie eine, wie Sie es normalerweise tun w\u00fcrden.',
        screenshot: '1.png',
        tip: 'Stellen Sie sicher, dass die Sokuji-Erweiterung in Chrome oder Edge installiert und aktiviert ist.',
      },
      {
        title: 'Mikrofonberechtigung erteilen',
        content:
          'Wenn Sokuji eine Mikrofonberechtigung anfordert, klicken Sie auf <strong>\u201eBeim Besuch der Website zulassen\u201c</strong>, um den Zugriff zu gew\u00e4hren.',
        screenshot: '2.png',
        tip: 'Diese Berechtigung ist erforderlich, damit Sokuji Ihr Audio f\u00fcr die \u00dcbersetzung verarbeiten kann.',
      },
      {
        title: 'Sokuji-Erweiterung \u00f6ffnen',
        content:
          'Klicken Sie auf das Sokuji-Erweiterungssymbol in der oberen rechten Ecke Ihres Browsers und klicken Sie dann auf <strong>\u201eOpen Sokuji\u201c</strong>, um das Seitenpanel zu starten.',
        screenshot: '3.png',
        tip: 'Die Sokuji-Seitenleiste \u00f6ffnet sich auf der rechten Seite Ihres Browserfensters.',
      },
      {
        title: 'Sokuji-Einstellungen konfigurieren',
        content:
          'Konfigurieren Sie in der Sokuji-Seitenleiste Ihre \u00dcbersetzungseinstellungen, einschlie\u00dflich Quell- und Zielsprache, KI-Anbieter und andere Einstellungen.',
        screenshot: '4.png',
        tip: 'Testen Sie Ihren Mikrofoneingang, um sicherzustellen, dass Sokuji das Audio korrekt empf\u00e4ngt.',
      },
      {
        title: 'Sokuji Virtual Microphone ausw\u00e4hlen',
        content:
          'Klicken Sie in den Audioeinstellungen von Google Meet auf das Mikrofonsymbol und w\u00e4hlen Sie <strong>Sokuji Virtual Microphone</strong> aus dem Dropdown-Men\u00fc.',
        screenshot: '5.png',
        tip: 'Wenn Sie das virtuelle Mikrofon nicht sehen, aktualisieren Sie die Seite und stellen Sie sicher, dass die Erweiterung aktiv ist.',
      },
    ],
    tips: {
      title: 'Tipps f\u00fcr beste Ergebnisse',
      items: [
        'Sprechen Sie deutlich und vermeiden Sie Hintergrundger\u00e4usche',
        'Machen Sie kurze Pausen zwischen S\u00e4tzen f\u00fcr bessere \u00dcbersetzungsgenauigkeit',
        'Testen Sie Ihr Audio vor wichtigen Besprechungen',
      ],
    },
    faq: {
      title: 'H\u00e4ufig gestellte Fragen',
      items: [
        {
          question: 'Warum kann ich Sokuji Virtual Microphone nicht sehen?',
          answer:
            'Stellen Sie sicher, dass die Sokuji-Erweiterung installiert und aktiv ist. Versuchen Sie, die Google Meet-Seite zu aktualisieren. Wenn das Problem weiterhin besteht, \u00fcberpr\u00fcfen Sie, ob Sie der Erweiterung Mikrofonberechtigungen erteilt haben.',
        },
        {
          question: 'K\u00f6nnen andere Teilnehmer beide Sprachen h\u00f6ren?',
          answer:
            'Standardmäßig hören Teilnehmer nur das übersetzte Audio. Wenn Sie in Sokujis Block „Sprache“ die „Durchleitung“ einschalten, hören die Teilnehmer Ihre Originalstimme gleichzeitig unter der Übersetzung. Diese Funktion ist nützlich, wenn Sie möchten, dass Zuhörer beide Sprachen für Kontext oder Überprüfung hören.',
        },
        {
          question: 'Funktioniert Sokuji mit Google Meet-Aufnahmen?',
          answer:
            'Ja, das \u00fcbersetzte Audio wird in die Besprechungsaufnahme aufgenommen, wenn Sie Sokuji Virtual Microphone verwenden.',
        },
        {
          question: 'Was ist die Durchleitung?',
          answer:
            'Die Durchleitung mischt Ihre eigene Stimme leiser unter die Übersetzung ins virtuelle Mikrofon, sodass Zuhörer beide Sprachen gleichzeitig hören. Sie ist eine Unterzeile von „Übersetzung, die das Gegenüber hört“ im Block „Sprache“ (Einstellungen „Schnell“ oder Einstellungen → Erweitert → Audio), mit einem Lautstärkeregler: standardmäßig 20 %, höchstens 60 %. Solange „Übersetzung, die das Gegenüber hört“ ausgeschaltet ist, ist sie ausgegraut. Dies ist besonders nützlich für zweisprachige Zielgruppen oder wenn Sie den emotionalen Ton und die Nuancen Ihrer Originalrede beibehalten möchten.',
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content:
        'Wenn Sie auf Probleme sto\u00dfen, besuchen Sie bitte unser <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub-Repository</a> oder kontaktieren Sie uns unter <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> f\u00fcr Unterst\u00fctzung.',
    },
  },
  uk: {
    pageTitle: '\u0412\u0438\u043a\u043e\u0440\u0438\u0441\u0442\u0430\u043d\u043d\u044f Sokuji \u0437 Google Meet',
    backLink: '\u041d\u0430\u0437\u0430\u0434 \u0434\u043e \u043f\u0456\u0434\u0442\u0440\u0438\u043c\u0443\u0432\u0430\u043d\u0438\u0445 \u0441\u0430\u0439\u0442\u0456\u0432',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '\u041e\u0433\u043b\u044f\u0434',
      content:
        '\u0426\u0435\u0439 \u043f\u043e\u0441\u0456\u0431\u043d\u0438\u043a \u0434\u043e\u043f\u043e\u043c\u043e\u0436\u0435 \u0432\u0430\u043c \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0432\u0430\u0442\u0438 \u0442\u0430 \u0432\u0438\u043a\u043e\u0440\u0438\u0441\u0442\u043e\u0432\u0443\u0432\u0430\u0442\u0438 Sokuji \u0437 Google Meet \u0434\u043b\u044f \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0443 \u0432 \u0440\u0435\u0430\u043b\u044c\u043d\u043e\u043c\u0443 \u0447\u0430\u0441\u0456 \u043f\u0456\u0434 \u0447\u0430\u0441 \u0432\u0430\u0448\u0438\u0445 \u0437\u0443\u0441\u0442\u0440\u0456\u0447\u0435\u0439.',
    },
    steps: [
      {
        title: '\u0412\u0456\u0434\u0432\u0456\u0434\u0430\u0439\u0442\u0435 Google Meet',
        content:
          '\u0412\u0456\u0434\u043a\u0440\u0438\u0439\u0442\u0435 \u0431\u0440\u0430\u0443\u0437\u0435\u0440 \u0456 \u043f\u0435\u0440\u0435\u0439\u0434\u0456\u0442\u044c \u043d\u0430 <code>meet.google.com</code>. \u041f\u0440\u0438\u0454\u0434\u043d\u0430\u0439\u0442\u0435\u0441\u044f \u0434\u043e \u0437\u0443\u0441\u0442\u0440\u0456\u0447\u0456 \u0430\u0431\u043e \u0440\u043e\u0437\u043f\u043e\u0447\u043d\u0456\u0442\u044c \u043d\u043e\u0432\u0443, \u044f\u043a \u0437\u0430\u0437\u0432\u0438\u0447\u0430\u0439.',
        screenshot: '1.png',
        tip: '\u041f\u0435\u0440\u0435\u043a\u043e\u043d\u0430\u0439\u0442\u0435\u0441\u044f, \u0449\u043e \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f Sokuji \u0432\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0442\u0430 \u0443\u0432\u0456\u043c\u043a\u043d\u0435\u043d\u043e \u0432 Chrome \u0430\u0431\u043e Edge.',
      },
      {
        title: '\u041d\u0430\u0434\u0430\u0439\u0442\u0435 \u0434\u043e\u0437\u0432\u0456\u043b \u043d\u0430 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d',
        content:
          '\u042f\u043a\u0449\u043e Sokuji \u0437\u0430\u043f\u0438\u0442\u0443\u0454 \u0434\u043e\u0437\u0432\u0456\u043b \u043d\u0430 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d, \u043d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c <strong>\u00ab\u0414\u043e\u0437\u0432\u043e\u043b\u0438\u0442\u0438 \u043f\u0456\u0434 \u0447\u0430\u0441 \u0432\u0456\u0434\u0432\u0456\u0434\u0443\u0432\u0430\u043d\u043d\u044f \u0441\u0430\u0439\u0442\u0443\u00bb</strong>, \u0449\u043e\u0431 \u043d\u0430\u0434\u0430\u0442\u0438 \u0434\u043e\u0441\u0442\u0443\u043f.',
        screenshot: '2.png',
        tip: '\u0426\u0435\u0439 \u0434\u043e\u0437\u0432\u0456\u043b \u043d\u0435\u043e\u0431\u0445\u0456\u0434\u043d\u0438\u0439 \u0434\u043b\u044f \u043e\u0431\u0440\u043e\u0431\u043a\u0438 \u0430\u0443\u0434\u0456\u043e Sokuji \u0434\u043b\u044f \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0443.',
      },
      {
        title: '\u0412\u0456\u0434\u043a\u0440\u0438\u0439\u0442\u0435 \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f Sokuji',
        content:
          '\u041d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c \u043d\u0430 \u0456\u043a\u043e\u043d\u043a\u0443 \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f Sokuji \u0443 \u043f\u0440\u0430\u0432\u043e\u043c\u0443 \u0432\u0435\u0440\u0445\u043d\u044c\u043e\u043c\u0443 \u043a\u0443\u0442\u043a\u0443 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430, \u043f\u043e\u0442\u0456\u043c \u043d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c <strong>\u00abOpen Sokuji\u00bb</strong>, \u0449\u043e\u0431 \u0432\u0456\u0434\u043a\u0440\u0438\u0442\u0438 \u0431\u0456\u0447\u043d\u0443 \u043f\u0430\u043d\u0435\u043b\u044c.',
        screenshot: '3.png',
        tip: '\u0411\u0456\u0447\u043d\u0430 \u043f\u0430\u043d\u0435\u043b\u044c Sokuji \u0432\u0456\u0434\u043a\u0440\u0438\u0454\u0442\u044c\u0441\u044f \u0432 \u043f\u0440\u0430\u0432\u0456\u0439 \u0447\u0430\u0441\u0442\u0438\u043d\u0456 \u0432\u0456\u043a\u043d\u0430 \u0431\u0440\u0430\u0443\u0437\u0435\u0440\u0430.',
      },
      {
        title: '\u041d\u0430\u043b\u0430\u0448\u0442\u0443\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u0438 Sokuji',
        content:
          '\u0423 \u0431\u0456\u0447\u043d\u0456\u0439 \u043f\u0430\u043d\u0435\u043b\u0456 Sokuji \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0439\u0442\u0435 \u043f\u0430\u0440\u0430\u043c\u0435\u0442\u0440\u0438 \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0443, \u0432\u043a\u043b\u044e\u0447\u0430\u044e\u0447\u0438 \u043c\u043e\u0432\u0438 \u0434\u0436\u0435\u0440\u0435\u043b\u0430 \u0442\u0430 \u0446\u0456\u043b\u0456, \u043f\u043e\u0441\u0442\u0430\u0447\u0430\u043b\u044c\u043d\u0438\u043a\u0430 \u0428\u0406 \u0442\u0430 \u0456\u043d\u0448\u0456 \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0432\u0430\u043d\u043d\u044f.',
        screenshot: '4.png',
        tip: '\u041f\u0435\u0440\u0435\u0432\u0456\u0440\u0442\u0435 \u0432\u0445\u0456\u0434 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d\u0430, \u0449\u043e\u0431 \u043f\u0435\u0440\u0435\u043a\u043e\u043d\u0430\u0442\u0438\u0441\u044f, \u0449\u043e Sokuji \u043f\u0440\u0430\u0432\u0438\u043b\u044c\u043d\u043e \u043e\u0442\u0440\u0438\u043c\u0443\u0454 \u0430\u0443\u0434\u0456\u043e.',
      },
      {
        title: '\u0412\u0438\u0431\u0435\u0440\u0456\u0442\u044c Sokuji Virtual Microphone',
        content:
          '\u0423 \u043d\u0430\u043b\u0430\u0448\u0442\u0443\u0432\u0430\u043d\u043d\u044f\u0445 \u0430\u0443\u0434\u0456\u043e Google Meet \u043d\u0430\u0442\u0438\u0441\u043d\u0456\u0442\u044c \u043d\u0430 \u0456\u043a\u043e\u043d\u043a\u0443 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d\u0430 \u0442\u0430 \u0432\u0438\u0431\u0435\u0440\u0456\u0442\u044c <strong>Sokuji Virtual Microphone</strong> \u0437 \u0432\u0438\u043f\u0430\u0434\u043d\u043e\u0433\u043e \u043c\u0435\u043d\u044e.',
        screenshot: '5.png',
        tip: '\u042f\u043a\u0449\u043e \u0432\u0438 \u043d\u0435 \u0431\u0430\u0447\u0438\u0442\u0435 \u0432\u0456\u0440\u0442\u0443\u0430\u043b\u044c\u043d\u0438\u0439 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d, \u043e\u043d\u043e\u0432\u0456\u0442\u044c \u0441\u0442\u043e\u0440\u0456\u043d\u043a\u0443 \u0442\u0430 \u043f\u0435\u0440\u0435\u043a\u043e\u043d\u0430\u0439\u0442\u0435\u0441\u044f, \u0449\u043e \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f \u0430\u043a\u0442\u0438\u0432\u043d\u0435.',
      },
    ],
    tips: {
      title: '\u041f\u043e\u0440\u0430\u0434\u0438 \u0434\u043b\u044f \u043d\u0430\u0439\u043a\u0440\u0430\u0449\u0438\u0445 \u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u0456\u0432',
      items: [
        '\u0413\u043e\u0432\u043e\u0440\u0456\u0442\u044c \u0447\u0456\u0442\u043a\u043e \u0442\u0430 \u0443\u043d\u0438\u043a\u0430\u0439\u0442\u0435 \u0444\u043e\u043d\u043e\u0432\u043e\u0433\u043e \u0448\u0443\u043c\u0443',
        '\u0420\u043e\u0431\u0456\u0442\u044c \u043a\u043e\u0440\u043e\u0442\u043a\u0456 \u043f\u0430\u0443\u0437\u0438 \u043c\u0456\u0436 \u0440\u0435\u0447\u0435\u043d\u043d\u044f\u043c\u0438 \u0434\u043b\u044f \u043a\u0440\u0430\u0449\u043e\u0457 \u0442\u043e\u0447\u043d\u043e\u0441\u0442\u0456 \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0443',
        '\u041f\u0440\u043e\u0442\u0435\u0441\u0442\u0443\u0439\u0442\u0435 \u0430\u0443\u0434\u0456\u043e \u043f\u0435\u0440\u0435\u0434 \u0432\u0430\u0436\u043b\u0438\u0432\u0438\u043c\u0438 \u0437\u0443\u0441\u0442\u0440\u0456\u0447\u0430\u043c\u0438',
      ],
    },
    faq: {
      title: '\u041f\u043e\u0448\u0438\u0440\u0435\u043d\u0456 \u0437\u0430\u043f\u0438\u0442\u0430\u043d\u043d\u044f',
      items: [
        {
          question: '\u0427\u043e\u043c\u0443 \u044f \u043d\u0435 \u0431\u0430\u0447\u0443 Sokuji Virtual Microphone?',
          answer:
            '\u041f\u0435\u0440\u0435\u043a\u043e\u043d\u0430\u0439\u0442\u0435\u0441\u044f, \u0449\u043e \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044f Sokuji \u0432\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u043e \u0442\u0430 \u0430\u043a\u0442\u0438\u0432\u043d\u0435. \u0421\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u043e\u043d\u043e\u0432\u0438\u0442\u0438 \u0441\u0442\u043e\u0440\u0456\u043d\u043a\u0443 Google Meet. \u042f\u043a\u0449\u043e \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u0430 \u043d\u0435 \u0437\u043d\u0438\u043a\u0430\u0454, \u043f\u0435\u0440\u0435\u0432\u0456\u0440\u0442\u0435, \u0447\u0438 \u043d\u0430\u0434\u0430\u043b\u0438 \u0432\u0438 \u0434\u043e\u0437\u0432\u0456\u043b \u043d\u0430 \u043c\u0456\u043a\u0440\u043e\u0444\u043e\u043d \u0440\u043e\u0437\u0448\u0438\u0440\u0435\u043d\u043d\u044e.',
        },
        {
          question: '\u0427\u0438 \u043c\u043e\u0436\u0443\u0442\u044c \u0456\u043d\u0448\u0456 \u0443\u0447\u0430\u0441\u043d\u0438\u043a\u0438 \u0447\u0443\u0442\u0438 \u043e\u0431\u0438\u0434\u0432\u0456 \u043c\u043e\u0432\u0438?',
          answer:
            'За замовчуванням учасники чують лише перекладене аудіо. Якщо увімкнути «Passthrough» у блоці «Мовлення» в Sokuji, учасники одночасно чутимуть і ваш оригінальний голос під перекладом. Ця функція корисна, коли ви хочете, щоб слухачі чули обидві мови для контексту або перевірки.',
        },
        {
          question: '\u0427\u0438 \u043f\u0440\u0430\u0446\u044e\u0454 Sokuji \u0456\u0437 \u0437\u0430\u043f\u0438\u0441\u0430\u043c\u0438 Google Meet?',
          answer:
            '\u0422\u0430\u043a, \u043f\u0435\u0440\u0435\u043a\u043b\u0430\u0434\u0435\u043d\u0435 \u0430\u0443\u0434\u0456\u043e \u0431\u0443\u0434\u0435 \u0432\u043a\u043b\u044e\u0447\u0435\u043d\u043e \u0434\u043e \u0437\u0430\u043f\u0438\u0441\u0443 \u0437\u0443\u0441\u0442\u0440\u0456\u0447\u0456, \u044f\u043a\u0449\u043e \u0432\u0438 \u0432\u0438\u043a\u043e\u0440\u0438\u0441\u0442\u043e\u0432\u0443\u0454\u0442\u0435 Sokuji Virtual Microphone.',
        },
        {
          question: 'Що таке Passthrough?',
          answer:
            'Passthrough підмішує ваш власний голос тихіше під переклад у віртуальний мікрофон, щоб слухачі чули обидві мови одночасно. Це підпункт рядка «Переклад, який чує співрозмовник» у блоці «Мовлення» (режим «Швидкий» у налаштуваннях або Налаштування → Розширений → Аудіо), з повзунком гучності: 20% за замовчуванням, не більше 60%. Поки «Переклад, який чує співрозмовник» вимкнено, він неактивний (сірий). Це особливо корисно для двомовної аудиторії або коли ви хочете зберегти емоційний тон та нюанси оригінальної мови.',
        },
      ],
    },
    troubleshooting: {
      title: '\u0423\u0441\u0443\u043d\u0435\u043d\u043d\u044f \u043d\u0435\u043f\u043e\u043b\u0430\u0434\u043e\u043a',
      content:
        '\u042f\u043a\u0449\u043e \u0443 \u0432\u0430\u0441 \u0432\u0438\u043d\u0438\u043a\u043b\u0438 \u043f\u0440\u043e\u0431\u043b\u0435\u043c\u0438, \u0432\u0456\u0434\u0432\u0456\u0434\u0430\u0439\u0442\u0435 \u043d\u0430\u0448 <a href="https://github.com/kizuna-ai-lab/sokuji">\u0440\u0435\u043f\u043e\u0437\u0438\u0442\u043e\u0440\u0456\u0439 GitHub</a> \u0430\u0431\u043e \u0437\u0432\u2019\u044f\u0436\u0456\u0442\u044c\u0441\u044f \u0437 \u043d\u0430\u043c\u0438 \u0437\u0430 \u0430\u0434\u0440\u0435\u0441\u043e\u044e <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> \u0434\u043b\u044f \u043e\u0442\u0440\u0438\u043c\u0430\u043d\u043d\u044f \u043f\u0456\u0434\u0442\u0440\u0438\u043c\u043a\u0438.',
    },
  },
  it: {
    pageTitle: 'Usare Sokuji con Google Meet',
    backLink: 'Torna ai siti supportati',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Panoramica',
      content:
        'Questa guida ti accompagner\u00e0 nella configurazione e nell\u2019uso di Sokuji con Google Meet per la traduzione linguistica in tempo reale durante le tue riunioni.',
    },
    steps: [
      {
        title: 'Visita Google Meet',
        content:
          'Apri il tuo browser e vai su <code>meet.google.com</code>. Partecipa o avvia una riunione come faresti normalmente.',
        screenshot: '1.png',
        tip: 'Assicurati che l\u2019estensione Sokuji sia installata e attivata in Chrome o Edge.',
      },
      {
        title: 'Concedi il permesso del microfono',
        content:
          'Se Sokuji richiede il permesso del microfono, fai clic su <strong>\u201cConsenti durante la visita del sito\u201d</strong> per concedere l\u2019accesso.',
        screenshot: '2.png',
        tip: 'Questo permesso \u00e8 necessario affinch\u00e9 Sokuji possa elaborare il tuo audio per la traduzione.',
      },
      {
        title: 'Apri l\u2019estensione Sokuji',
        content:
          'Fai clic sull\u2019icona dell\u2019estensione Sokuji nell\u2019angolo in alto a destra del tuo browser, quindi fai clic su <strong>\u201cOpen Sokuji\u201d</strong> per aprire il pannello laterale.',
        screenshot: '3.png',
        tip: 'La barra laterale di Sokuji si aprir\u00e0 sul lato destro della finestra del browser.',
      },
      {
        title: 'Configura le impostazioni di Sokuji',
        content:
          'Nella barra laterale di Sokuji, configura le impostazioni di traduzione, incluse le lingue di origine e destinazione, il fornitore di IA e altre preferenze.',
        screenshot: '4.png',
        tip: 'Testa l\u2019ingresso del microfono per assicurarti che Sokuji stia ricevendo correttamente l\u2019audio.',
      },
      {
        title: 'Seleziona Sokuji Virtual Microphone',
        content:
          'Nelle impostazioni audio di Google Meet, fai clic sull\u2019icona del microfono e seleziona <strong>Sokuji Virtual Microphone</strong> dal menu a tendina.',
        screenshot: '5.png',
        tip: 'Se non vedi il microfono virtuale, aggiorna la pagina e assicurati che l\u2019estensione sia attiva.',
      },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Parla chiaramente ed evita il rumore di fondo',
        'Fai una breve pausa tra le frasi per una migliore precisione della traduzione',
        'Testa il tuo audio prima di riunioni importanti',
      ],
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        {
          question: 'Perch\u00e9 non riesco a vedere Sokuji Virtual Microphone?',
          answer:
            'Assicurati che l\u2019estensione Sokuji sia installata e attiva. Prova ad aggiornare la pagina di Google Meet. Se il problema persiste, verifica di aver concesso i permessi del microfono all\u2019estensione.',
        },
        {
          question: 'Gli altri partecipanti possono sentire entrambe le lingue?',
          answer:
            'Per impostazione predefinita, i partecipanti sentono solo l’audio tradotto. Se attivi “Passthrough” nel blocco “Voce” di Sokuji, i partecipanti sentiranno anche la tua voce originale sotto la traduzione, nello stesso momento. Questa funzione è utile quando vuoi che gli ascoltatori sentano entrambe le lingue per contesto o verifica.',
        },
        {
          question: 'Sokuji funziona con le registrazioni di Google Meet?',
          answer:
            'S\u00ec, l\u2019audio tradotto verr\u00e0 incluso nella registrazione della riunione se stai usando Sokuji Virtual Microphone.',
        },
        {
          question: 'Cos’è il Passthrough?',
          answer:
            'Il Passthrough mescola la tua voce, a un volume più basso, sotto la traduzione nel microfono virtuale, così gli ascoltatori sentono entrambe le lingue insieme. È una sottoriga di “Traduzione che sente l\'altra parte” nel blocco “Voce” (impostazioni “Rapido”, oppure Impostazioni → Avanzato → Audio), con un cursore del volume: 20% per impostazione predefinita, al massimo 60%. Resta in grigio finché “Traduzione che sente l\'altra parte” è disattivata. Questo è particolarmente utile per un pubblico bilingue o quando vuoi mantenere il tono emotivo e le sfumature del tuo discorso originale.',
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content:
        'Se riscontri problemi, visita il nostro <a href="https://github.com/kizuna-ai-lab/sokuji">repository GitHub</a> o contattaci all\u2019indirizzo <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> per ricevere supporto.',
    },
  },
};

export function GoogleMeetTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/google-meet" />;
}
